// Het Benchmarks-tabblad: per MSI-effect of de klantdrempel gehaald is, en zo
// niet wat er nog voor nodig is om een trede te stijgen.
//
// Waarom dit tabblad bestaat: een inzicht over een effect is pas relevant als de
// benchmark van dát effect sterk genoeg is. De ISS-cellen zeggen iets over een
// patroon binnen een effect; deze lijst zegt of het effect als geheel al genoeg
// vergelijkingsbasis heeft om er überhaupt iets over te beweren.
//
// Die regel is sinds ADR-040 een POORT en niet langer alleen een zin hier:
// publiceren wordt geweigerd zolang `thresholdMet` false is. De weigering komt
// van de server (functions/intelligence/effectBenchmarkGate.js); het scherm
// leest hem via src/helpers/intelligence/effectBenchmarkGate.js.
// AI-CONTEXT: See docs/decisions.md ADR-040
//
// De tekorten komen uit getBenchmarkNextLevelGaps: die meldt alleen de kolom(men)
// die de trede werkelijk tegenhouden. Een sterkere kolom verbeteren tilt het
// niveau niet, want het niveau is het minimum over de drie.
//
// AI-CONTEXT: See docs/methodiek-benchmark-model.md

import { MIN_CLIENT_STRENGTH_SCORE } from '../dataHelpers/getBenchmarkStrength'
import { EFFECT, formatBenchmarkValue } from '../dashboard/buildBenchmarkRows'
import { getSectorInfo } from '../meetstandaard/sectorCatalog'
import telwoord from './telwoord'

/**
 * Wat er nog nodig is, als zin. Leeg wanneer er niets te halen valt.
 *
 * Het concentratie-tekort komt als negatief getal binnen (het aandeel moet
 * omlaag). Dat als "nog -10" tonen zou het omkeren, dus die krijgt een eigen
 * formulering in plaats van een plek in de opsomming.
 *
 * @param {{organisations: (number|null), respondents: (number|null), largestShare: (number|null)}} [gaps]
 * @returns {string}
 */
const watOntbreekt = (gaps) => {
    const g = gaps || {}
    const delen = []
    if (typeof g.organisations === 'number' && g.organisations > 0) {
        delen.push(telwoord(g.organisations, 'organisatie', 'organisaties'))
    }
    if (typeof g.respondents === 'number' && g.respondents > 0) {
        delen.push(telwoord(g.respondents, 'respondent', 'respondenten'))
    }

    const concentratie = (typeof g.largestShare === 'number' && g.largestShare < 0)
        ? `Het aandeel van de grootste organisatie moet ${Math.abs(g.largestShare)} procentpunt omlaag`
        : ''

    if (delen.length && concentratie) {
        return `Nog ${delen.join(' en ')} voor de volgende trede. ${concentratie}.`
    }
    if (delen.length) return `Nog ${delen.join(' en ')} voor de volgende trede.`
    if (concentratie) return `${concentratie} voor de volgende trede.`
    return ''
}

/**
 * @typedef {object} BenchmarkOverviewRow
 * @property {string} rowId
 * @property {string} kind - 'effect', 'groei' of 'niveau' (zie buildBenchmarkRows)
 * @property {string} output - Materialen-output ('co2Kg'/'privateEur'), anders ''
 * @property {string} effectName
 * @property {string} category
 * @property {number} companyCount - Bijdragende organisaties
 * @property {number} respondentCount - Respondenten
 * @property {number} strengthScore - 0–3
 * @property {string} strengthLabel - Weergavelabel van de sterkte
 * @property {boolean} judgeable - Is er een sterktescore om over te oordelen
 * @property {boolean} thresholdMet - Haalt de benchmark de klantdrempel
 * @property {string} gapText - Wat er nog nodig is, of '' op de hoogste trede
 * @property {Array<string>} tags - Soort ('effect'/'output') gevolgd door de sectorlabels
 * @property {string} valueText - De benchmarkwaarde met eenheid, of '—' als er geen is
 * @property {Array<string>} sectors - Ruwe sector-slugs, om op te filteren
 */

/**
 * Benchmarkrijen omzetten naar het overzicht, sterkste eerst.
 *
 * @param {Array<object>} [rows] - Rijen uit useMsiBenchmarkRows
 * @returns {BenchmarkOverviewRow[]}
 */
const buildBenchmarkOverview = (rows) => {
    if (!Array.isArray(rows)) return []

    return rows
        .map((row) => {
            const bron = row || {}
            // Geen sterktescore is iets anders dan score 0. Materialen dragen er
            // bewust geen: het benchmarkmodel laat open welke teleenheid telt bij
            // een niveau-benchmark, en die verzinnen zou de rij sterker laten
            // lijken dan hij is. Zo'n rij krijgt dus géén oordeel — niet 'niet
            // gehaald', want dat is óók een oordeel.
            const judgeable = typeof bron.strengthScore === 'number'
            const score = judgeable ? bron.strengthScore : 0
            return {
                rowId: bron.rowId || bron.effectId || '',
                // De soort, om op te splitsen; de tags zijn labels voor de lezer.
                kind: bron.kind || '',
                // Alleen materialen dragen er een; de sleutel van hun detailpagina.
                output: bron.output || '',
                effectId: bron.effectId || '',
                effectName: bron.effectName || '',
                category: bron.category || '',
                companyCount: bron.companyCount || 0,
                respondentCount: bron.respondentCount || 0,
                strengthScore: score,
                strengthLabel: bron.strengthLabel || '',
                judgeable,
                thresholdMet: judgeable && score >= MIN_CLIENT_STRENGTH_SCORE,
                gapText: judgeable ? watOntbreekt(bron.nextLevelGaps) : '',
                // Soort eerst, dan de sectoren. Een onbekende sector krijgt
                // geen tag: liever niets dan een verzonnen label.
                // De ruwe slugs blijven: de tags zijn labels voor de lezer,
                // filteren gebeurt op de slug.
                sectors: Array.isArray(bron.sectors) ? bron.sectors : [],
                // Wat de benchmark zégt, niet alleen of hij sterk genoeg is.
                // Eén formatter voor alle soorten: een output is nooit een
                // percentage en een ontbrekende waarde nooit een nul.
                valueText: formatBenchmarkValue(bron),
                tags: [
                    bron.kind === EFFECT ? 'effect' : 'output',
                    // De outputfamilie, uit 'Output · arbeidsvaardigheden'. De
                    // negen arbeidsvaardigheden-rijen heten naar hun eigen
                    // vaardigheid; zonder dit is niet te zien dat ze bij elkaar
                    // horen. Staat er geen familie in de categorie, dan ook geen
                    // tag — liever niets dan een verzonnen groep.
                    ...(bron.kind !== EFFECT && typeof bron.category === 'string' && bron.category.includes('·')
                        ? [bron.category.split('·').pop().trim()]
                        : []),
                    ...(Array.isArray(bron.sectors) ? bron.sectors : [])
                        .map((slug) => getSectorInfo(slug).label)
                        .filter(Boolean),
                ],
            }
        })
        // Wat de drempel haalt bovenaan: dat zijn de effecten waarover een
        // inzicht überhaupt iets mag zeggen. Daarna op nabijheid.
        // Volgorde, in deze prioriteit:
        //   1. wat gemeten is boven wat leeg is — een effect dat niemand meet
        //      hoort niet boven een output met echte huishoudens
        //   2. beoordeelbaar boven niet-beoordeelbaar — wat we niet kunnen wegen
        //      staat niet tussen de standen alsof het de zwakste is
        //   3. daarna op sterkte en omvang
        .sort((a, b) =>
            (b.respondentCount > 0 ? 1 : 0) - (a.respondentCount > 0 ? 1 : 0)
            || (b.judgeable ? 1 : 0) - (a.judgeable ? 1 : 0)
            || b.strengthScore - a.strengthScore
            || b.companyCount - a.companyCount
            || b.respondentCount - a.respondentCount
            || a.rowId.localeCompare(b.rowId))
}

/**
 * De MSI-effecten waarvan de benchmark de klantdrempel haalt.
 *
 * Alleen effecten: de Agenda toont ISS-cellen, en die bestaan alleen per effect.
 * Een output die de drempel haalt geeft dus geen cellen vrij.
 *
 * @param {Array<object>} [rows] - Rijen uit useBenchmarkRows
 * @returns {Array<string>} effect-ids (msiId)
 */
export const qualifyingEffectIds = (rows) => buildBenchmarkOverview(rows)
    .filter((row) => row.judgeable && row.thresholdMet && row.tags[0] === 'effect' && row.effectId)
    .map((row) => row.effectId)

export default buildBenchmarkOverview
